import React from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {changePreferencesVisibility} from 'modules/overlay/actions'

import {NavBar, NavBarTitle} from '@vio/atlas/NavBar'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {Page} from '@vio/core/src/analytics/types/Events'
import {
  getUser,
  selectAuthEnabled,
  selectIsAuthenticated
} from '@vio/core/src/auth/modules/selectors'
import {NavBarBackButton, NavBarBrandLogo} from '@vio/shared/src/navBar'
import {BrandLogo} from '@vio/shared/src/whiteLabel/BrandLogo/BrandLogo'

import {MobileHeaderMenu} from './MobileHeaderMenu'

interface Props {
  /** Override the default center content (defaults to the brand logo) */
  centerContent?: React.ReactElement
  hasHeaderMenu?: boolean
  disableLogoClick?: boolean
  onBackButtonClick?: () => void
  pageName?: Page
  showStartLogo?: boolean
}

export const MobileHeader = ({
  hasHeaderMenu = true,
  disableLogoClick = false,
  centerContent,
  onBackButtonClick,
  showStartLogo = true,
  pageName
}: Props) => {
  const isPaymentPage = pageName === Page.Payment
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const dispatch = useDispatch()
  const {brand, brandSiteAddress} = useBrand()
  const user = useSelector(getUser)
  const authEnabled = useSelector(selectAuthEnabled)

  const shouldShowEndContent = hasHeaderMenu
  const handleShowPreferences = () => {
    dispatch(changePreferencesVisibility(true, false)())
  }

  const EndContent = () => {
    if (
      (shouldShowEndContent && isPaymentPage && isAuthenticated) ||
      (shouldShowEndContent && !isPaymentPage)
    ) {
      return (
        <MobileHeaderMenu
          shouldShowUserAvatar={authEnabled}
          user={user || undefined}
          onShowPreferences={handleShowPreferences}
          hideBurgerMenu={isPaymentPage}
        />
      )
    }
    return null
  }

  // Replicates the legacy CompactHeader slot logic: a back button takes over
  // the start slot; otherwise a compact logo accompanies custom center content,
  // while the full logo is centered when there is none.
  const startContent = onBackButtonClick ? (
    <NavBarBackButton onClick={onBackButtonClick} />
  ) : centerContent && showStartLogo ? (
    <NavBarBrandLogo
      compact
      brand={brand}
      logoUrl={brandSiteAddress}
      disableLogoClick={disableLogoClick}
      BrandLogoComponent={BrandLogo}
    />
  ) : undefined

  return (
    <NavBar
      centerAligned
      startContent={startContent}
      centerContent={
        centerContent ? (
          <NavBarTitle>{centerContent}</NavBarTitle>
        ) : (
          <NavBarBrandLogo
            brand={brand}
            logoUrl={brandSiteAddress}
            disableLogoClick={disableLogoClick}
            BrandLogoComponent={BrandLogo}
          />
        )
      }
      // The legacy header dropped the end slot outside the payment page so the
      // center content could expand over it
      endContent={isPaymentPage ? <EndContent /> : undefined}
    />
  )
}
